import React, { useEffect } from 'react';
import { FaTimes, FaExpand, FaCompress } from 'react-icons/fa';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  title?: string;
  isMobile?: boolean;
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoUrl, title, isMobile = false }) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Handle fullscreen
  const toggleFullscreen = async () => {
    if (!modalRef.current) return;

    if (!isFullscreen) {
      try {
        await modalRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch (err) {
        console.error('Error exiting fullscreen:', err);
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!isOpen) return null;

  // Convert YouTube URLs to embed format
  const getEmbedUrl = (url: string | null | undefined) => {
    // Check if url is valid
    if (!url || typeof url !== 'string') {
      console.error('VideoModal: Invalid video URL:', url);
      return '';
    }
    
    // YouTube video ID extraction
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    
    // Vimeo video ID extraction
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // Return original URL if no match
    return url;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        ref={modalRef}
        className={`relative w-full ${isMobile ? 'h-full' : 'max-w-4xl h-[80vh] mx-4'} bg-white dark:bg-gray-800 ${isMobile ? '' : 'rounded-lg'} shadow-2xl overflow-hidden`}
      >
        {/* Header */}
        <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent ${isMobile ? 'p-3 safe-area-pt' : 'p-4'} flex justify-between items-center z-10`}>
          <h3 className="text-white font-semibold text-lg">
            {title || 'Video'}
          </h3>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
              >
                {isFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            )}
            <button
              onClick={onClose}
              className={`${isMobile ? 'p-3' : 'p-2'} rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors`}
              title="Kapat"
              aria-label="Kapat"
            >
              <FaTimes size={isMobile ? 24 : 20} />
            </button>
          </div>
        </div>
        
        {/* Video iframe */}
        <iframe
          src={getEmbedUrl(videoUrl)}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={title || 'Video'}
        />
      </div>
    </div>
  );
};

export default VideoModal;